import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

export interface IDeviceOnlinePollingState {
  /** device_id (no hyphens) being polled */
  deviceId: string;
  /** Date.now() when polling started — used to enforce 2-minute cap */
  startedAt: number;
}

export interface IClinicStateSlice {
  error: unknown;
  clinicState: IReduxLoadingState;
  clinicList: IClinic[];
  userClinic?: IClinic;
  clinicFloorList: IClinicFloor[];
  loadingFloors: number[];
  selectedFloors: null | number[];
  redirectToUserInfo: boolean;
  assignedDevice: IAssignedDevice | null;
  /** Non-null while the app is polling to detect the newly-provisioned device coming online. */
  deviceOnlinePolling: IDeviceOnlinePollingState | null;
  /** True after polling finishes without the device ever reporting is_online: true. */
  devicePollingTimedOut: boolean;
}

const name = "clinic";

export const initialState: IClinicStateSlice = {
  clinicState: "pending",
  error: "",
  clinicList: [],
  userClinic: undefined,
  clinicFloorList: [],
  loadingFloors: [],
  selectedFloors: null,
  redirectToUserInfo: false,
  assignedDevice: null,
  deviceOnlinePolling: null,
  devicePollingTimedOut: false,
};

export const clinicSlice = createSlice({
  name,
  initialState,
  reducers: {
    setUserClinic(state, action: PayloadAction<{ clinic: IClinic }>) {
      state.userClinic = action.payload.clinic;
    },
    setRedirectToUserInfo(state, action: PayloadAction<boolean>) {
      state.redirectToUserInfo = action.payload;
    },
    setClinicList(state, action: PayloadAction<{ clinicList: IClinic[] }>) {
      state.clinicList = action.payload.clinicList;
    },
    setClinicFloorList(
      state,
      action: PayloadAction<{ clinicFloorList: IClinicFloor[] }>
    ) {
      state.clinicFloorList = action.payload.clinicFloorList;
      return state;
    },
    setSelectedFloors(state, action: PayloadAction<number[]>) {
      state.selectedFloors = action.payload;
    },
    setClinicState: (
      state,
      action: PayloadAction<Partial<IClinicStateSlice>>
    ) => ({
      ...state,
      ...action.payload,
    }),
    setAssignedDevice(state, action: PayloadAction<IAssignedDevice | null>) {
      state.assignedDevice = action.payload;
    },
    startDeviceOnlinePolling(
      state,
      action: PayloadAction<{ deviceId: string }>
    ) {
      state.deviceOnlinePolling = {
        deviceId: action.payload.deviceId,
        startedAt: Date.now(),
      };
      // Clear any previous timeout banner when a new poll starts
      state.devicePollingTimedOut = false;
    },
    stopDeviceOnlinePolling(state) {
      state.deviceOnlinePolling = null;
    },
    setDevicePollingTimedOut(state, action: PayloadAction<boolean>) {
      state.devicePollingTimedOut = action.payload;
    },
    setClinicSliceReset() {
      return initialState;
    },
  },
  extraReducers(builder) {},
});

export const {
  setUserClinic,
  setClinicList,
  setClinicFloorList,
  setSelectedFloors,
  setClinicState,
  setClinicSliceReset,
  setRedirectToUserInfo,
  setAssignedDevice,
  startDeviceOnlinePolling,
  stopDeviceOnlinePolling,
  setDevicePollingTimedOut,
} = clinicSlice.actions;

const persistedReducer = persistReducer(
  {
    key: name,
    storage: storage,
    whitelist: ["selectedFloors", "clinicList", "userClinic", "assignedDevice"],
  },
  clinicSlice.reducer
);

export default persistedReducer;
